/**
 * KYC notice three-state machine (signup / login wall / pending banner).
 * Display-only policy. Does not mint, delete, or lock commissions.
 * Duplicate-account removal is out of scope.
 */

import {
  isPurchaseKycAdminAttested,
  isPurchaseKycSubmitted,
  isPurchaseKycGrandfatheredExistingUser,
  normalizePurchaseKycStatus,
} from './purchaseKycGate.js'

export const KYC_NOTICE_NONE = 'none'
export const KYC_NOTICE_DISMISSIBLE = 'dismissible'
export const KYC_NOTICE_WALL = 'wall'
export const KYC_NOTICE_PENDING_BANNER = 'pending_banner'

export const KYC_NOTICE_TITLE = 'KYC Verification Required'

export const KYC_NOTICE_PARAGRAPHS = [
  'To ensure compliance with legal and regulatory requirements, KYC verification is now mandatory before any purchase can be completed.',
  'Each individual is permitted to have one account only, and all account holders must be 18 years of age or older. Accounts may not be created or operated on behalf of minors.',
  'No commission withdrawals or other withdrawals will be permitted until KYC verification has been successfully completed.',
  'Any duplicate accounts identified within the system will be reviewed. Commissions on confirmed duplicate accounts may be frozen pending admin action. Accounts are not deleted automatically.',
  'These requirements are in place to protect the integrity of the platform, ensure accurate identification of all account holders, and maintain legal and regulatory compliance.',
]

export const KYC_PENDING_BANNER_TEXT =
  'Your KYC has been submitted and is under review. You can purchase now. Withdrawals and selling stay blocked until an admin approves your KYC.'

const DONE_STATUSES = new Set(['completed', 'approved'])
const PENDING_STATUSES = new Set(['pending'])
const WALL_STATUSES = new Set(['not_started', 'rejected', 'expired'])

export function normalizeKycNoticeStatus(value) {
  return normalizePurchaseKycStatus(value) || 'not_started'
}

/**
 * @param {{
 *   lookupFailed?: boolean,
 *   exempt?: boolean,
 *   grandfathered?: boolean,
 *   kycStatus?: string | null,
 *   adminAttested?: boolean,
 *   purchaseCompliant?: boolean,
 * }} input
 */
export function resolveKycNoticeState(input = {}) {
  if (input.lookupFailed === true || input.exempt === true) {
    return KYC_NOTICE_NONE
  }

  const status = normalizeKycNoticeStatus(input.kycStatus)
  const attested = input.adminAttested === true
  const purchaseCompliant = input.purchaseCompliant === true

  if (input.grandfathered === true) {
    if (DONE_STATUSES.has(status) && attested) return KYC_NOTICE_NONE
    return KYC_NOTICE_DISMISSIBLE
  }

  if (purchaseCompliant) return KYC_NOTICE_NONE
  if (PENDING_STATUSES.has(status)) return KYC_NOTICE_PENDING_BANNER
  if (DONE_STATUSES.has(status) && !attested) return KYC_NOTICE_PENDING_BANNER
  if (WALL_STATUSES.has(status)) return KYC_NOTICE_WALL
  return KYC_NOTICE_WALL
}

export function buildKycCompliancePayload({
  createdAt,
  kycRow,
  approvedDocumentCount,
  exempt,
  lookupFailed,
}) {
  if (lookupFailed) {
    return {
      ok: true,
      lookup_failed: true,
      exempt: !!exempt,
      grandfathered: false,
      kyc_status: 'lookup_failed',
      admin_attested: false,
      purchase_compliant: false,
      notice_state: KYC_NOTICE_NONE,
      rejection_reason: null,
    }
  }

  const grandfathered = isPurchaseKycGrandfatheredExistingUser(createdAt)
  const status = normalizeKycNoticeStatus(kycRow?.kyc_status)
  const adminAttested = isPurchaseKycAdminAttested(kycRow, { approvedDocumentCount })
  const purchaseCompliant =
    grandfathered || isPurchaseKycSubmitted(kycRow)
  const noticeState = resolveKycNoticeState({
    lookupFailed: false,
    exempt,
    grandfathered,
    kycStatus: status,
    adminAttested,
    purchaseCompliant,
  })

  return {
    ok: true,
    lookup_failed: false,
    exempt: !!exempt,
    grandfathered,
    kyc_status: kycRow ? status : 'not_started',
    admin_attested: adminAttested,
    purchase_compliant: purchaseCompliant,
    notice_state: noticeState,
    rejection_reason: kycRow?.rejection_reason ? String(kycRow.rejection_reason) : null,
  }
}
